// Sites: several domains, each with its own rooms and its own default room at `/` (docs/sites-and-domains.md).
// One rooms.json lists every site (`sites`) and every room (`site` per room), so room ids are unique across sites and
// one room server serves them all. One client build serves every site; the page's hostname picks the site.
//
// A door to a room of another site is a full page load on that site's domain. What the URL carries
// (ARRIVAL_PARAMS): the room we came from and the arrival door, the visitor's name and colour (localStorage is per
// domain), and the music item and position. The page on the other domain reads them once and removes them.
//
// Hosts that are not listed (localhost, 127.0.0.1, *.pages.dev previews) are not a site: every room is reachable
// there and doors never leave the page, as before sites. `<site id>.localhost` is that site's host in local dev
// (browsers resolve *.localhost to this machine), so cross-site doors can be tried locally.

import { MAX_NAME_LENGTH, ROOM_ID_RE } from "./protocol";
import { cleanText } from "./text";
import type { RoomsFile } from "./rooms";

/** The part of the WHATWG URL used here. shared/ has no DOM or Node types; browsers and Node both have URL. */
interface UrlLike {
  href: string; hostname: string; protocol: string; port: string; pathname: string; search: string; hash: string;
  searchParams: {
    get(k: string): string | null; set(k: string, v: string): void; append(k: string, v: string): void;
    delete(k: string): void; forEach(cb: (v: string, k: string) => void): void;
  };
}
declare const URL: { new (url: string, base?: string): UrlLike };

const isId = (v: unknown): v is string => typeof v === "string" && ROOM_ID_RE.test(v);

/** The site a listed room belongs to (`site`, else the first site); null without sites or for an unlisted room. */
export function roomSite(f: RoomsFile, room: string): string | null {
  const ids = f.sites ? Object.keys(f.sites) : [];
  const r = f.rooms[room];
  if (!ids.length || !r) return null;
  return r.site ?? ids[0]!;
}

/** The site served on `hostname` (listed in a site's hosts, or `<site id>.localhost`); null for other hosts. */
export function hostSite(f: RoomsFile, hostname: string): string | null {
  if (!f.sites) return null;
  const h = hostname.toLowerCase();
  for (const [id, s] of Object.entries(f.sites)) if (s.hosts.includes(h)) return id;
  const m = /^([a-z0-9_-]{1,64})\.localhost$/.exec(h);
  return m && f.sites[m[1]!] ? m[1]! : null;
}

/** The room at `/`: the site's default room, else the file's. */
export function siteDefaultRoom(f: RoomsFile, site: string | null): string {
  return (site && f.sites?.[site]?.default_room) || f.default_room;
}

/** `https://<host>` for every site host: the room server's allowed origins (with the pages.dev and dev ones). */
export function siteOrigins(f: RoomsFile): string[] {
  return Object.values(f.sites ?? {}).flatMap((s) => s.hosts.map((h) => `https://${h}`));
}

/** What a cross-site door carries to the other domain. All optional. */
export interface Arrival {
  /** room id we came from (the arrival door is the target's door that leads back to it) */
  from?: string;
  /** portal id to arrive at (the link's `to_spawn`) */
  spawn?: string;
  name?: string;
  /** `#rrggbb` */
  color?: string;
  /** music item id */
  media?: string;
  /** music position in seconds */
  t?: number;
}

export const ARRIVAL_PARAMS = ["from", "spawn", "name", "color", "media", "t"] as const;
/** Page parameters that belong to one room's first load and are not carried to another site. */
const DROP_PARAMS = ["room", "site", "scene", "channel", "build", ...ARRIVAL_PARAMS];

/**
 * The URL on the other site's domain for a door to `target`, or null when the door stays in this page (same site,
 * the page is not on a site host, or the target's site has no host yet). Other page parameters (`?server=`,
 * `?portals=1`, ...) are kept.
 */
export function crossSiteUrl(f: RoomsFile, target: string, here: UrlLike, carry: Arrival = {}): string | null {
  const to = roomSite(f, target);
  const at = hostSite(f, here.hostname);
  if (!to || !at || to === at) return null;
  let origin: string;
  if (here.hostname.toLowerCase().endsWith(".localhost") && !f.sites![at]!.hosts.includes(here.hostname.toLowerCase())) {
    origin = `${here.protocol}//${to}.localhost${here.port ? `:${here.port}` : ""}`;
  } else {
    const host = f.sites![to]!.hosts[0];
    if (!host) return null;
    origin = `https://${host}`;
  }
  const u = new URL(target === siteDefaultRoom(f, to) ? "/" : `/${encodeURIComponent(target)}`, origin);
  here.searchParams.forEach((v, k) => { if (!DROP_PARAMS.includes(k)) u.searchParams.append(k, v); });
  if (carry.from && isId(carry.from)) u.searchParams.set("from", carry.from);
  if (carry.spawn && isId(carry.spawn)) u.searchParams.set("spawn", carry.spawn);
  const name = carry.name ? cleanText(carry.name, MAX_NAME_LENGTH) : "";
  if (name) u.searchParams.set("name", name);
  if (carry.color && /^#[0-9a-f]{6}$/i.test(carry.color)) u.searchParams.set("color", carry.color.toLowerCase());
  if (carry.media) u.searchParams.set("media", carry.media.slice(0, 200));
  if (carry.t !== undefined && Number.isFinite(carry.t) && carry.t >= 0) u.searchParams.set("t", carry.t.toFixed(1));
  return u.href;
}

/** The arrival parameters of a page URL (validated), and the URL without them (path + query + hash). */
export function readArrival(url: UrlLike): { arrival: Arrival | null; clean: string } {
  const q = url.searchParams;
  const a: Arrival = {};
  const from = q.get("from"), spawn = q.get("spawn"), name = q.get("name"), color = q.get("color"), media = q.get("media"), t = q.get("t");
  if (isId(from)) a.from = from;
  if (isId(spawn)) a.spawn = spawn;
  // the server's name rule (normalizeName): a name it rejects would keep the visitor from joining
  const n = name ? cleanText(name, MAX_NAME_LENGTH) : "";
  if (n) a.name = n;
  if (color && /^#[0-9a-f]{6}$/i.test(color)) a.color = color.toLowerCase();
  if (media) a.media = media.slice(0, 200);
  if (t !== null && Number.isFinite(Number(t)) && Number(t) >= 0) a.t = Number(t);
  const u = new URL(url.href);
  for (const k of ARRIVAL_PARAMS) u.searchParams.delete(k);
  return { arrival: Object.keys(a).length ? a : null, clean: u.pathname + u.search + u.hash };
}
